/**
 * A whole-days histogram, charted (L1) — shared by the two `DefenderAgeBand[]`
 * the incident panel publishes, which carry the same honesty obligations and the
 * same band edges:
 *
 * - **how long open incidents have been open** (`age_bands`), and
 * - **how long closed incidents took** (`close_time_bands`).
 *
 * One component rather than two near-copies, because the parts that are easy to
 * get wrong are identical in both:
 *
 * - **An empty list is no data, not a row of zeroes.** The backend emits no
 *   bands at all when nothing was counted, so the chart is replaced by an
 *   `EmptyState` that says which — never a zero-height histogram, which reads as
 *   "measured, and everything is fine".
 * - **The `unknown` band is held off the colour ramp.** A row whose timestamps
 *   could not be used has no duration, and placing it on a duration ramp would
 *   invent one. It takes the muted token instead (§3.4).
 * - **The x axis is ordinal and stays in payload order.** Sorting a histogram by
 *   value destroys the only thing it shows.
 * - **`truncated` makes every count a floor** (§8), so the tooltip reads "at
 *   least" and the note says so beside the numbers.
 */

import { Chart } from "@norican/dataviz";
import { EmptyState } from "@norican/react";
import type { ReactNode } from "react";

import { ChartCard } from "@/components/board";
import type { DefenderAgeBand } from "@/lib/api";

import { ageBandFill, formatCount, formatPercent, tooltipValue, TRUNCATED_NOTE } from "./status";

/** One plotted band, with its fill and its share of what was counted. */
interface BandRow {
  label: string;
  count: number;
  share: number | null;
  bounds: string;
  color: string;
}

export interface BandsPanelProps {
  /** Card heading. One series, so no legend — this names it (§6). */
  title: string;
  /** The payload's bands, quickest/youngest first. */
  bands: readonly DefenderAgeBand[];
  /** What one unit is, for captions: "open incidents", "closed incidents". */
  noun: string;
  /** Chart accessible name — required on every `Chart.Container` (§6). */
  ariaLabel: string;
  /** Board-unique table-twin id from `TWIN_IDS`. */
  twinId: string;
  /** `collected_at` of the panel these bands came from. */
  since?: string | null;
  /** Staleness threshold for the `Freshness` dot, in ms. */
  staleAfterMs?: number;
  /** True when the panel's paging stopped at NOVA's cap — counts are floors. */
  truncated: boolean;
  /** Extra caption text after the count, e.g. what the axis measures. */
  caption: string;
  /** What the `unknown` band holds here, rendered only when it is populated. */
  unknownNote: string;
  /** An extra note under the chart, e.g. a proxy caveat from the payload. */
  footnote?: ReactNode;
  /** What to say when nothing was counted — never a zero-height histogram. */
  emptyTitle: string;
  emptyDescription: string;
}

export function BandsPanel({
  title,
  bands,
  noun,
  ariaLabel,
  twinId,
  since,
  staleAfterMs,
  truncated,
  caption,
  unknownNote,
  footnote,
  emptyTitle,
  emptyDescription,
}: BandsPanelProps) {
  const rows = buildBandRows(bands);
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return (
    <ChartCard<BandRow>
      title={title}
      caption={
        rows.length === 0
          ? "Nothing was counted"
          : `${formatCount(total)} ${noun}${truncated ? " or more" : ""} · ${caption}`
      }
      since={since}
      staleAfterMs={staleAfterMs}
      tableTwin={{
        id: twinId,
        ariaLabel: `${title} — every band with its bounds and count`,
        data: rows,
        getRowId: (row) => row.label,
        columns: [
          { id: "band", header: "Band", accessor: (row) => row.label },
          { id: "bounds", header: "Bounds (days)", accessor: (row) => row.bounds },
          {
            id: "count",
            header: truncated ? "Count (floor)" : "Count",
            align: "end",
            accessor: (row) => row.count,
            cell: (row) => <span className="tabular-nums">{formatCount(row.count)}</span>,
          },
          {
            id: "share",
            header: "Share of what was read",
            align: "end",
            accessor: (row) => row.share,
            cell: (row) => <span className="tabular-nums">{formatPercent(row.share)}</span>,
          },
        ],
      }}
    >
      {rows.length === 0 ? (
        <EmptyState size="sm" title={emptyTitle} description={emptyDescription} />
      ) : (
        <div className="flex flex-col gap-3">
          <Chart.Container
            config={Object.fromEntries(
              rows.map((row) => [row.label, { label: row.label, color: row.color }]),
            )}
            className="h-64"
            aria-label={ariaLabel}
          >
            <Chart.BarChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" interval={0} />
              <Chart.YAxis {...Chart.axisProps} width={44} allowDecimals={false} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    // `tooltipValue` first, never `Number(value)`: a band with no
                    // value arrives as `undefined`, and `Number(null)` is a
                    // fabricated 0.
                    formatter={(value) =>
                      `${truncated ? "at least " : ""}${formatCount(tooltipValue(value))} ${noun}`
                    }
                  />
                }
              />
              <Chart.Bar {...Chart.seriesDefaults} dataKey="count" radius={[3, 3, 0, 0]}>
                {rows.map((row) => (
                  <Chart.Cell key={row.label} fill={row.color} />
                ))}
              </Chart.Bar>
            </Chart.BarChart>
          </Chart.Container>

          {rows.some((row) => row.bounds === "unknown") && (
            <p className="text-fg-muted text-sm">
              The <span className="text-fg font-medium">unknown</span> band {unknownNote} It takes
              the muted token rather than a rung on the ramp, because placing it on a duration scale
              would invent a duration for it.
            </p>
          )}

          {footnote}

          {truncated && <p className="text-fg-muted text-sm">{TRUNCATED_NOTE}</p>}
        </div>
      )}
    </ChartCard>
  );
}

/**
 * Project a band histogram into chart rows.
 *
 * Bands arrive quickest/youngest first and stay in that order — a duration
 * histogram's x axis is ordinal, so sorting it by value would destroy the only
 * thing it shows. The fill is a sequential ramp over that order (§3.4:
 * sequential = magnitude), with the `unknown` band held off the ramp entirely.
 */
function buildBandRows(bands: readonly DefenderAgeBand[]): BandRow[] {
  const populated = bands.filter((band) => band.count > 0);
  const total = populated.reduce((sum, band) => sum + band.count, 0);
  return populated.map((band, index) => {
    const isUnknown = band.lower_days == null && band.upper_days == null;
    return {
      label: band.label,
      count: band.count,
      share: total > 0 ? (band.count / total) * 100 : null,
      bounds: isUnknown
        ? "unknown"
        : band.upper_days == null
          ? `${formatCount(band.lower_days)} and older`
          : `${formatCount(band.lower_days)} to under ${formatCount(band.upper_days)}`,
      color: ageBandFill(index, populated.length, isUnknown),
    };
  });
}
